import { useState } from 'react';
import { starsLabel, type StarsConfig, type StarsValue } from '../../lib/fieldValue';
import type { Field } from '../../lib/templates';
import { FootnoteNote } from './FootnoteNote';

interface StarsPickerProps {
  field: Pick<Field, 'label' | 'allowFootnote'>;
  config: StarsConfig;
  value: StarsValue | null;
  onCommit: (value: StarsValue | null) => void;
  disabled?: boolean;
}

/** `0..max` buttons, each showing the filled/empty glyph run plus the label for that count. */
export function StarsPicker({ field, config, value, onCommit, disabled = false }: StarsPickerProps) {
  const [note, setNote] = useState(value?.note ?? '');
  const stars = value?.stars ?? null;

  function select(n: number) {
    if (disabled) {
      return;
    }
    onCommit({ type: 'STARS', stars: n, footnote: value?.footnote ?? false, note: value?.note ?? null });
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-1.5" role="group" aria-label={`${field.label} stars`}>
        {Array.from({ length: config.max + 1 }, (_, n) => n).map((n) => (
          <button
            key={n}
            type="button"
            disabled={disabled}
            aria-pressed={stars === n}
            onClick={() => select(n)}
            className={[
              'flex min-h-tap flex-col items-center gap-0.5 rounded-md border px-2 py-1.5 transition-colors disabled:cursor-not-allowed disabled:opacity-50',
              stars === n
                ? 'border-accent bg-accent-dim text-accent'
                : 'border-border-strong text-text-muted hover:border-accent-border hover:text-text',
            ].join(' ')}
          >
            <span aria-hidden="true">{'★'.repeat(n) + '☆'.repeat(config.max - n)}</span>
            <span className="eyebrow">{starsLabel(n, config)}</span>
          </button>
        ))}
      </div>
      {field.allowFootnote && (
        <FootnoteNote
          footnote={value?.footnote ?? false}
          note={note}
          disabled={disabled || !value}
          onFootnoteChange={(footnote) => value && onCommit({ ...value, footnote })}
          onNoteChange={setNote}
          onNoteBlur={() => value && onCommit({ ...value, note: note.trim() === '' ? null : note })}
        />
      )}
    </div>
  );
}
